import { Component, OnInit, HostListener } from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import { Title } from '@angular/platform-browser';
import { MenuItem, MessageService } from 'primeng/api';
import { NavigationService } from '../../../../service/navigation.service';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import { AdvancedSettingsService } from '../../../../service/advanced-settings.service';
import { AccountService } from '../../../../service/account.service';
import { CopyToClipboardService } from '../../../../service/utilities/copy-to-clipboard.service';
import { FeatureFlagGuard } from '../../../../feature-flag.guard';
import { AccountInterface } from '../../../../model/account.interface';

@Component({
    selector: 'auto-advanced-settings',
    templateUrl: './advanced-settings.component.html',
    styleUrls: ['./advanced-settings.component.scss'],
    standalone: false
})
export class AdvancedSettingsComponent implements OnInit {
  private static readonly DISCLOSURE_SETTING_VIEW_FLAG = 'disclosureSettingView';

  accountId: number;
  settingsSideNav: MenuItem[];
  // Data object passed down to child tabs via [data]
  advancedSettings: any;
  // Child forms
  adConfigForm: UntypedFormGroup;
  pageConfigForm: UntypedFormGroup;
  leadMgmtForm: UntypedFormGroup;
  serviceCategoriesForm: UntypedFormGroup;
  generalForm: UntypedFormGroup;
  disclosuresForm: UntypedFormGroup;
  account: AccountInterface;
  showDisclosuresTab = false;
  preCacheBusy = false;
  saveSettingsLoading = false;
  showDisclosureConfirmDialog = false;

  constructor(
    private titleService: Title,
    private route: ActivatedRoute,
    protected formBuilder: UntypedFormBuilder,
    private navigationService: NavigationService,
    protected messageService: MessageService,
    protected accountService: AccountService,
    protected copyToClipboardService: CopyToClipboardService,
    protected featureFlagGuard: FeatureFlagGuard,
    private advancedSettingsService: AdvancedSettingsService,
  ) {
  }

  /**
   * Loads the feature flag, fetches advanced settings and account data, and initialises the page title.
   */
  async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Advanced');
    this.route.paramMap.subscribe((params) => {
      this.accountId = parseInt(params.get('accountId'));
      this.initNavigation();
    });
    this.showDisclosuresTab = await this.featureFlagGuard.isFeatureOn(
      AdvancedSettingsComponent.DISCLOSURE_SETTING_VIEW_FLAG
    );
    this.advancedSettingsService.getAdvancedSettings(this.accountId).subscribe({
      next: (settings) => {
        this.accountService.getAccount(this.accountId).subscribe({
          next: (account) => {
            this.advancedSettings = settings;
            this.account = account;
          },
          error: () => {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load advanced settings'
            });
          },
        });
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load advanced settings'
        });
      },
    });
  }


  /**
   * Loads the side-navigation menu items for the current account.
   */
  initNavigation(): void {
    this.navigationService
      .getMenuItems(this.accountId, 'settings-view-side-menu.configuration.json')
      .then((navItems) => (this.settingsSideNav = navItems));
  }

  /**
   * Receives the Ad Configuration child form and stores it for save aggregation.
   */
  onAdConfigFormGroupInitialized(form: UntypedFormGroup) {
    this.adConfigForm = form;
  }

  /**
   * Receives the Page Configuration child form and stores it for save aggregation.
   */
  onPageConfigFormGroupInitialized(form: UntypedFormGroup) {
    this.pageConfigForm = form;
  }

  /**
   * Receives the Lead Management child form and stores it for save aggregation.
   */
  onLeadManagementFormGroupInitialized(form: UntypedFormGroup) {
    this.leadMgmtForm = form;
  }

  /**
   * Receives the Service Categories child form and stores it for save aggregation.
   */
  onServiceCategoriesFormGroupInitialized(form: UntypedFormGroup) {
    this.serviceCategoriesForm = form;
  }

  /**
   * Receives the General child form and stores it for save aggregation.
   */
  onGeneralFormGroupInitialized(form: UntypedFormGroup) {
    this.generalForm = form;
  }

  /**
   * Receives the Disclosures child form and stores it for save aggregation.
   */
  onDisclosuresFormGroupInitialized(form: UntypedFormGroup) {
    this.disclosuresForm = form;
  }

  /**
   * Returns true when no child form has unsaved valid changes, disabling the save button.
   */
  getSaveButtonDisabled(): boolean {
    const forms = [
      this.adConfigForm,
      this.pageConfigForm,
      this.leadMgmtForm,
      this.serviceCategoriesForm,
      this.generalForm,
      this.disclosuresForm
    ].filter(Boolean) as UntypedFormGroup[];

    const anyDirtyAndValid = forms.some((form) => form.dirty && form.valid);
    return !anyDirtyAndValid;
  }

  /**
   * Handles the save button click — shows the disclosure sync confirmation dialog
   * when the disclosures form is dirty, otherwise saves immediately.
   */
  onSaveButtonClicked(): void {
    let disclosuresFormIsDirty = false;

    if (this.disclosuresForm) {
      disclosuresFormIsDirty = this.disclosuresForm.dirty;
    }

    if (disclosuresFormIsDirty) {
      this.showDisclosureConfirmDialog = true;
    } else {
      this.saveAdvancedSettings();
    }
  }

  /**
   * Called when the user confirms they want to sync disclosure changes; closes the
   * dialog and proceeds with a full save including disclosure sync.
   */
  onDisclosureSaveConfirmed(): void {
    this.showDisclosureConfirmDialog = false;
    this.saveAdvancedSettings();
  }

  /**
   * Called when the user chooses to save without syncing disclosures; closes the
   * dialog and saves with the sync step skipped.
   */
  onDisclosureSkipUpdates(): void {
    this.showDisclosureConfirmDialog = false;
    this.saveAdvancedSettings(true);
  }

  /**
   * Called when the user cancels the disclosure sync dialog without saving.
   */
  onDisclosureConfirmCancelled(): void {
    this.showDisclosureConfirmDialog = false;
  }

  /**
   * Submits all child form values to the API, marks all forms pristine on success,
   * and shows a toast notification for both success and failure outcomes.
   */
  saveAdvancedSettings(skipDisclosureSync = false) {
    this.saveSettingsLoading = true;
    const updatedAdvancedSettings = this.buildUpdatedAdvancedSettings(skipDisclosureSync);
    this.advancedSettingsService.updateAdvancedSettings(updatedAdvancedSettings).subscribe({
      next: () => {
        this.saveSettingsLoading = false;
        [
          this.adConfigForm,
          this.pageConfigForm,
          this.leadMgmtForm,
          this.serviceCategoriesForm,
          this.generalForm,
          this.disclosuresForm
        ]
          .filter(Boolean)
          .forEach((f: UntypedFormGroup) => f.markAsPristine());
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Advanced settings saved successfully'
        });
      },
      error: () => {
        this.saveSettingsLoading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save advanced settings'
        });
      }
    });
  }

  /**
   * Aggregates all child form values into a single settings payload ready to send to the server.
   */
  private buildUpdatedAdvancedSettings(skipDisclosureSync = false): any {
    const adConfigValue = this.adConfigForm?.getRawValue() || {};
    const pageConfigValue = this.pageConfigForm?.getRawValue() || {};
    const leadMgmtValue = this.leadMgmtForm?.getRawValue() || {};
    const serviceCatgoriesValues = this.serviceCategoriesForm?.getRawValue() || {};
    const generalFormValue = this.generalForm?.getRawValue() || {};
    const disclosuresValue = this.disclosuresForm?.getRawValue() || {};
    const disclosureFields = this.buildGuardedDisclosurePayload(disclosuresValue);
    const focusModelIds = this.normalizeFocusModelIds(generalFormValue.focusModelIds);

    return {
      accountId: this.accountId,

      ...disclosureFields,
      ...this.buildEnhancedDisclosurePayload(adConfigValue, disclosuresValue),

      // Offer Set Configuration
      accountOfferSetRefresh: !!adConfigValue.accountOfferSetRefresh,
      customTitleDisplayOverride: !!adConfigValue.customTitleDisplayOverride,
      newOfferSetCustomTitleOverrideText: String(adConfigValue.newOfferSetCustomTitleOverrideText ?? ''),
      usedOfferSetCustomTitleOverrideText: String(adConfigValue.usedOfferSetCustomTitleOverrideText ?? ''),
      cpoOfferSetCustomTitleOverrideText: String(adConfigValue.cpoOfferSetCustomTitleOverrideText ?? ''),
      minimizeDisclaimer: !!adConfigValue.minimizeDisclaimer,
      newMainCtaText: String(adConfigValue.newMainCtaText ?? ''),
      usedMainCtaText: String(adConfigValue.usedMainCtaText ?? ''),
      sharedOfferSetsEnabled: !!adConfigValue.sharedOfferSetsEnabled,

      // pageConfigValue Configuration
      newUsedWhyBuyMessage: String(pageConfigValue.whyBuyMessage ?? ''),
      serviceWhyBuyMessage: String(pageConfigValue.serviceWhyBuyMessage ?? ''),
      dealerLicenseName: String(pageConfigValue.dealerLicenseName ?? ''),
      ardNumber: String(pageConfigValue.ardNumber ?? ''),
      servicePhoneNumber: String(pageConfigValue.servicePhoneNumber ?? ''),
      serviceDisclosure: String(pageConfigValue.serviceDisclosure ?? ''),
      pageHeaderMainCtaText: String(pageConfigValue.pagesMainCtaText ?? ''),
      headerMainCtaText: String(pageConfigValue.inventoryMainCtaText ?? ''),
      showUsedOnNewVlps: !!pageConfigValue.newVlpShowUsed,
      showFormWhenNoOfferSetsAvailable: !!pageConfigValue.showDefaultForm,

      // Lead Management
      ccpaConsentEnabled: !!leadMgmtValue.leadFormConsentEnabled,
      ccpaConsentText: String(leadMgmtValue.leadFormConsentText ?? ''),
      tcpaConsentEnabled: !!leadMgmtValue.detailedLeadFormConsentEnabled,
      tcpaConsentText: String(leadMgmtValue.detailedLeadFormConsentText ?? ''),
      newFormMainCtaText: String(leadMgmtValue.newFormMainCtaText ?? ''),
      usedFormMainCtaText: String(leadMgmtValue.usedFormMainCtaText ?? ''),
      requirePostalCodeOnLeadForms: !!leadMgmtValue.leadsNeedPostalCode,
      requirePreferredContactOnLeadForm: !!leadMgmtValue.preferredContactRequired,
      leadFormTfaEnabled: !!leadMgmtValue.leadFormTfaEnabled,

      // Service Categories
      accountAvailableServiceCategories: Array.isArray(serviceCatgoriesValues.accountAvailableServiceCategories)
      ? serviceCatgoriesValues.accountAvailableServiceCategories
        : [],

      // General
      preCacheSpecials: !!generalFormValue.preCacheSpecials,
      emailToolVersion: Number.isFinite(generalFormValue.emailVersion) ? Number(generalFormValue.emailVersion) : 1,
      enhancedSeoEnabled: !!generalFormValue.enhancedSeoEnabled,
      inAcuraProgram: !!generalFormValue.inAcuraProgram,
      inAudiProgram: !!generalFormValue.inAudiProgram,
      inHondaProgram: !!generalFormValue.inHondaProgram,
      inFordDirectProgram: !!generalFormValue.inFordDirectProgram,
      fordDirectDealerId: String(generalFormValue.fordDirectDealerId ?? ''),
      stellantisReportingTag: !!generalFormValue.stellantisReportingTag,
      showDashboardReport: !!generalFormValue.showDashboardReport,
      adobeTaggingEnabled: !!generalFormValue.adobeTaggingEnabled,
      dynamicCreativeEnabled: !!generalFormValue.dynamicCreativeEnabled,
      maintainCreativeStatusForFocusModels: !!generalFormValue.maintainCreativeStatusForFocusModels,
      focusModelIds,
      mediaAdCopyBulkUpdateEnabled: !!generalFormValue.mediaAdCopyBulkUpdateEnabled,
      skipDisclosureSync,
    };
  }

  /**
   * Normalizes Focus Model selections for the complete-state PUT contract.
   */
  private normalizeFocusModelIds(modelIds: unknown): number[]
  {
    const normalizedModelIds = new Set<number>();

    if (Array.isArray(modelIds)) {
      for (const modelId of modelIds) {
        if (typeof modelId === 'number' && Number.isInteger(modelId) && modelId > 0) {
          normalizedModelIds.add(modelId);
        }
      }
    }

    const sortedModelIds = Array.from(normalizedModelIds);
    sortedModelIds.sort((first, second) => first - second);

    return sortedModelIds;
  }

  /**
   * Enhanced Disclosure and Disclosure Set live in the Disclosures tab when the
   * disclosureSettingView flag is on, and in the Offer Sets tab otherwise.
   */
  private buildEnhancedDisclosurePayload(adConfigValue: any, disclosuresValue: any): any {
    let source = adConfigValue;

    if (this.showDisclosuresTab && this.disclosuresForm) {
      source = disclosuresValue;
    }

    let disclosureSet = 0;

    if (Number.isFinite(source.disclosureSet)) {
      disclosureSet = Number(source.disclosureSet);
    }

    return {
      disclosureSet,
      enhancedDisclosure: !!source.enhancedDisclosure,
    };
  }

  /**
   * Returns disclosure fields from the active disclosures form when the tab is enabled,
   * or falls back to the last-loaded advancedSettings values to ensure the DTO is always
   * fully hydrated — preventing PHP from throwing on uninitialized non-nullable properties.
   */
  private buildGuardedDisclosurePayload(disclosuresFormValue: any): object
  {
    let disclosureFields: object = this.buildDisclosurePayload(this.advancedSettings || {});

    if (this.showDisclosuresTab && this.disclosuresForm) {
      disclosureFields = this.buildDisclosurePayload(disclosuresFormValue);
    }

    return disclosureFields;
  }

  /**
   * Maps the raw disclosures form value to the flat server-side field names for new and used ad defaults.
   */
  private buildDisclosurePayload(disclosuresValue: any): any {
    return {
      // New Ad Defaults
      newShowDealInfoInDisclaimer: !!disclosuresValue.newShowDealInfoInDisclaimer,
      newShowDaysToFirstPaymentInDisclosure: !!disclosuresValue.newShowDaysToFirstPaymentInDisclosure,
      newFinanceDisclosure: String(disclosuresValue.newFinanceDisclosure ?? ''),
      newBuyDisclosure: String(disclosuresValue.newBuyDisclosure ?? ''),
      newAprDisclosure: String(disclosuresValue.newAprDisclosure ?? ''),
      newLeaseDisclosure: String(disclosuresValue.newLeaseDisclosure ?? ''),
      newAlternateLeaseDisclosure: String(disclosuresValue.newAlternateLeaseDisclosure ?? ''),
      newDisclosureAddon: String(disclosuresValue.newDisclosureAddon ?? ''),
      newShowPhotosForIllustration: !!disclosuresValue.newShowPhotosForIllustration,
      newShowAllAvailableStocks: !!disclosuresValue.newShowAllAvailableStocks,
      newShowTotalAvailableStocks: !!disclosuresValue.newShowTotalAvailableStocks,
      newShowLastEightVin: !!disclosuresValue.newShowLastEightVin,
      newShowPricingSuperscripts: !!disclosuresValue.newShowPricingSuperscripts,
      newSavingsDisclosure: String(disclosuresValue.newSavingsDisclosure ?? ''),
      // Used Ad Defaults
      usedFinanceDisclosure: String(disclosuresValue.usedFinanceDisclosure ?? ''),
      usedShowDaysToFirstPaymentInDisclosure: !!disclosuresValue.usedShowDaysToFirstPaymentInDisclosure,
      usedBuyDisclosure: String(disclosuresValue.usedBuyDisclosure ?? ''),
      usedAprDisclosure: String(disclosuresValue.usedAprDisclosure ?? ''),
      usedSavingsDisclosure: String(disclosuresValue.usedSavingsDisclosure ?? ''),
      usedShowDealsInDisclaimers: !!disclosuresValue.usedShowDealsInDisclaimers,
      usedLeaseDisclosure: String(disclosuresValue.usedLeaseDisclosure ?? ''),
      usedAlternateLeaseDisclosure: String(disclosuresValue.usedAlternateLeaseDisclosure ?? ''),
      usedDisclosureAddon: String(disclosuresValue.usedDisclosureAddon ?? ''),
      usedShowPhotosForIllustration: !!disclosuresValue.usedShowPhotosForIllustration,
      usedShowLastEightVin: !!disclosuresValue.usedShowLastEightVin,
      usedShowPricingSuperscripts: !!disclosuresValue.usedShowPricingSuperscripts,
    };
  }

  /**
   * Triggers a server-side specials cache refresh and reloads settings on completion.
   * Prevents concurrent requests while a refresh is already in progress.
   */
  onPreCacheRequested(): void {
    if (!this.preCacheBusy) {
      this.preCacheBusy = true;

      this.advancedSettingsService.refreshCache(this.accountId).subscribe({
        next: () => {
          this.messageService.add({
            severity: 'success',
            summary: 'Pre Cache Validation',
            detail: 'Cache refresh completed.'
          });
          this.advancedSettingsService.getAdvancedSettings(this.accountId).subscribe({
            next: (settings) => (this.advancedSettings = settings)
          });
        },
        error: () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Pre Cache Validation',
            detail: 'Failed to refresh cache.'
          });
        },
        complete: () => {
          this.preCacheBusy = false;
        }
      });
    }
  }

  /**
   * Returns true if any child form has unsaved changes, used to gate the beforeunload warning.
   */
  private hasAnyUnsavedChanges(): boolean {
    const forms = [
      this.adConfigForm,
      this.pageConfigForm,
      this.leadMgmtForm,
      this.serviceCategoriesForm,
      this.generalForm,
      this.disclosuresForm,
    ].filter(Boolean) as UntypedFormGroup[];

    return forms.some((form) => form.dirty);
  }

  /**
   * Triggers the native browser leave-page prompt when there are unsaved changes.
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasAnyUnsavedChanges()) {
      const message = 'Are you sure you want to leave this page? Unsaved changes will be lost.';
      event.returnValue = message;
    }
  }
}
